import { memo, useMemo } from 'react'
import { IconCategory, IconChevronRight } from '@tabler/icons-react'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'

import { NavMenuCategory } from 'modules/products'
import { cssColors } from 'shared/ui/utils/cssTokens'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import styles from './CategoriesNavigationTreeItem.module.scss'

interface CategoryTreeItemProps {
  category: NavMenuCategory
  focusedCategory: NavMenuCategory | null
  level: number
  href: string
  pathname: string
  onFocusCategory: (category: NavMenuCategory, level: number) => void
  showChevron?: boolean
}

const CategoryNavigationTreeItem = memo(
  ({
    category,
    focusedCategory,
    level,
    href,
    pathname,
    onFocusCategory,
    showChevron = true
  }: CategoryTreeItemProps) => {
    const isFocused = useMemo(
      () => focusedCategory?.id === category.id,
      [focusedCategory, category]
    )

    const isSubcategoryFocused = useMemo((): boolean => {
      if (!focusedCategory?.parentId && !category.id) {
        return false
      }

      return focusedCategory?.parentId === category.id
    }, [focusedCategory, category])

    return (
      <li className={styles.wrapper} role='none'>
        <Link
          className={clsx(styles.item, {
            [styles.active]: isFocused || isSubcategoryFocused
          })}
          id={`category-menu-item-${category.id}`}
          href={href}
          role='treeitem'
          tabIndex={isFocused ? 0 : -1}
          aria-current={pathname === href ? 'page' : undefined}
          aria-expanded={isFocused && isNotNilOrEmpty(category.subCategories)}
          aria-haspopup={isNotNilOrEmpty(category.subCategories)}
          onFocus={() => onFocusCategory(category, level)}
          onMouseEnter={() => onFocusCategory(category, level)}
          {...addTestId(`category-menu-item-level-${level}`)}
          aria-owns={`category-menu-column-${level + 1}`}
        >
          <p className={styles.label}>
            {level === 1 &&
              (category.id === 'all-products' ? (
                <IconCategory
                  width={24}
                  height={24}
                  color={cssColors.brand.secondary}
                />
              ) : (
                <Image
                  src={category.icon?.path || '/static/question-mark.svg'}
                  width={24}
                  height={24}
                  alt={category.name}
                />
              ))}
            {category.name}
            {category.productsCount !== undefined &&
              ` (${category.productsCount})`}
          </p>

          {showChevron && isNotNilOrEmpty(category.subCategories) && (
            <IconChevronRight />
          )}
        </Link>
      </li>
    )
  }
)

export default CategoryNavigationTreeItem
